import React, { useState, useEffect, useMemo } from 'react';
import {
  getFirestore, collection, query, where, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, arrayRemove, serverTimestamp, orderBy, limit, writeBatch,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import {
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserGroupIcon,
  UserCircleIcon,
  StarIcon,
  XMarkIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  ClockIcon as ClockSolidIcon,
  BoltIcon,
  CheckIcon,
  PaperAirplaneIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

const formatDate = (ts) => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

const fmt$ = (v) => (v == null || v === '' ? '—' : `$${Number(v).toLocaleString()}`);

const brokerDisplayName = (b) =>
  (b.firstName && b.lastName ? `${b.firstName} ${b.lastName}` : '')
  || b.brokerAgeName || b.name || b.businessName || b.companyName || 'Unknown Broker';

export default function BrokersTab({ pendingBrokers = [], onOpenFollowups, onHidePendingBrokers }) {
  const db = getFirestore();
  const storage = getStorage();

  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('activity'); // activity | name | deals
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedBrokerId, setSelectedBrokerId] = useState(null); // optional individual focus inside an org
  const [showBrokerInvite, setShowBrokerInvite] = useState(false);
  const [brokerInviteSeed, setBrokerInviteSeed] = useState(null);

  // Multi-select for bulk-removing organizations + pending brokers from this tab.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState(() => new Set());
  const [selectedPendingIds, setSelectedPendingIds] = useState(() => new Set());
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const toggleOrgSelection = (orgId) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId); else next.add(orgId);
      return next;
    });
  };
  const togglePendingSelection = (pendingId) => {
    setSelectedPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(pendingId)) next.delete(pendingId); else next.add(pendingId);
      return next;
    });
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedOrgIds(new Set());
    setSelectedPendingIds(new Set());
  };
  const totalSelected = selectedOrgIds.size + selectedPendingIds.size;

  const openBrokerInvite = (broker = null) => {
    setBrokerInviteSeed(broker ? {
      id: broker.id,
      name: broker.name || brokerDisplayName(broker),
      email: broker.email || '',
      phoneNumber: broker.phoneNumber || '',
      brokerageName: broker.brokerageName || '',
    } : null);
    setShowBrokerInvite(true);
  };

  const handleBrokerCreated = (createdBroker) => {
    setBrokers((current) => {
      const index = current.findIndex((broker) => broker.id === createdBroker.id || broker.email === createdBroker.email);
      if (index === -1) return [...current, createdBroker];
      return current.map((broker, position) => position === index ? { ...broker, ...createdBroker } : broker);
    });
  };

  // Detail state (aggregated across the org)
  const [orgDeals, setOrgDeals] = useState([]);
  const [orgPrequals, setOrgPrequals] = useState([]);
  const [orgComms, setOrgComms] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedFiles, setExpandedFiles] = useState({});
  const [uploadingFor, setUploadingFor] = useState(null);

  // Inline edit state for brokerage name + per-row delete-in-flight tracking.
  const [editingBrokerageName, setEditingBrokerageName] = useState(false);
  const [brokerageNameDraft, setBrokerageNameDraft] = useState('');
  const [savingBrokerageName, setSavingBrokerageName] = useState(false);
  const [deletingRecordId, setDeletingRecordId] = useState(null);

  // -------- Initial load: all brokers + counts --------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'brokers'));
        const list = [];
        for (const d of snap.docs) {
          const data = d.data();
          if (data.hideFromBrokerTab === true) continue;
          list.push({
            id: d.id,
            name: brokerDisplayName(data),
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            brokerageName: data.brokerageName || data.brokerAgeName || data.companyName || '',
            phoneNumber: data.phoneNumber || '',
            createdAt: data.createdAt || null,
            lastActive: data.lastActive || data.updatedAt || data.createdAt || null,
            parentBrokerId: data.parentBrokerId || null,
            ...data,
          });
        }

        const counts = await Promise.all(list.map(async (b) => {
          try {
            const dealsSnap = await getDocs(query(
              collection(db, 'loanApplications'),
              where('brokerId', '==', b.id),
              where('source', '==', 'broker_deal'),
            ));
            const prequalIds = Array.isArray(b.prequalificationApplications) ? b.prequalificationApplications.length : 0;
            return { id: b.id, dealsCount: dealsSnap.size, prequalsCount: prequalIds };
          } catch {
            return { id: b.id, dealsCount: 0, prequalsCount: 0 };
          }
        }));
        const countMap = Object.fromEntries(counts.map(c => [c.id, c]));
        const enriched = list.map(b => ({ ...b, ...(countMap[b.id] || { dealsCount: 0, prequalsCount: 0 }) }));

        if (!cancelled) setBrokers(enriched);
      } catch (err) {
        console.error('Error loading brokers:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [db]);

  // -------- Group brokers into organizations --------
  const organizations = useMemo(() => {
    if (!brokers.length) return [];
    // orgId = parentBrokerId if member is a sub-broker, otherwise own id
    const orgMap = new Map();
    for (const b of brokers) {
      const orgId = b.parentBrokerId || b.id;
      if (!orgMap.has(orgId)) orgMap.set(orgId, { id: orgId, members: [], creator: null });
      orgMap.get(orgId).members.push(b);
    }
    // Resolve creator (broker whose id == orgId) and org metadata
    const orgs = [];
    for (const [orgId, info] of orgMap.entries()) {
      let creator = info.members.find(m => m.id === orgId) || null;
      // If no creator entry found, fall back to first member or a placeholder
      if (!creator && info.members.length) creator = info.members[0];
      const subs = info.members.filter(m => m.id !== (creator?.id || orgId));
      const brokerageName = creator?.brokerageName || info.members.find(m => m.brokerageName)?.brokerageName || 'Independent';
      const totalDeals = info.members.reduce((sum, m) => sum + (m.dealsCount || 0), 0);
      const totalPrequals = info.members.reduce((sum, m) => sum + (m.prequalsCount || 0), 0);
      const lastActive = info.members
        .map(m => m.lastActive?.toDate?.() || (m.lastActive ? new Date(m.lastActive) : null))
        .filter(Boolean)
        .sort((a, b) => b - a)[0] || null;
      orgs.push({
        id: orgId,
        brokerageName,
        creator,
        subBrokers: subs,
        members: info.members,
        totalDeals,
        totalPrequals,
        lastActive,
      });
    }
    return orgs;
  }, [brokers]);

  const filteredOrganizations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = organizations;
    if (q) {
      list = list.filter(org =>
        (org.brokerageName || '').toLowerCase().includes(q) ||
        (org.creator?.name || '').toLowerCase().includes(q) ||
        (org.creator?.email || '').toLowerCase().includes(q) ||
        org.members.some(m => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      );
    }
    if (sortBy === 'name') {
      list = [...list].sort((a, b) => (a.brokerageName || '').localeCompare(b.brokerageName || ''));
    } else if (sortBy === 'deals') {
      list = [...list].sort((a, b) => (b.totalDeals + b.totalPrequals) - (a.totalDeals + a.totalPrequals));
    } else {
      list = [...list].sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
    }
    return list;
  }, [organizations, searchTerm, sortBy]);

  const selectedOrg = useMemo(
    () => organizations.find(o => o.id === selectedOrgId) || null,
    [organizations, selectedOrgId]
  );

  const focusedMembers = useMemo(() => {
    if (!selectedOrg) return [];
    if (!selectedBrokerId) return selectedOrg.members;
    return selectedOrg.members.filter(m => m.id === selectedBrokerId);
  }, [selectedOrg, selectedBrokerId]);

  // -------- Load detail for the selected org (across all members) --------
  useEffect(() => {
    if (!selectedOrg) return;
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      setOrgDeals([]);
      setOrgPrequals([]);
      setOrgComms([]);
      setExpandedId(null);
      setExpandedFiles({});
      try {
        const memberIds = selectedOrg.members.map(m => m.id);
        const memberEmails = selectedOrg.members.map(m => m.email).filter(Boolean);

        const dealPromises = memberIds.map(id => getDocs(query(
          collection(db, 'loanApplications'),
          where('brokerId', '==', id),
          where('source', '==', 'broker_deal'),
        )));
        const prequalPromises = memberIds.map(id => getDocs(query(
          collection(db, 'prequalificationApplications'),
          where('brokerId', '==', id),
        )));
        const commPromises = memberEmails.map(email => getDocs(query(
          collection(db, 'email'),
          where('to', '==', email),
          orderBy('createdAt', 'desc'),
          limit(25),
        )).catch(() => ({ docs: [] })));

        const [dealsSnaps, prequalsSnaps, commsSnaps] = await Promise.all([
          Promise.all(dealPromises),
          Promise.all(prequalPromises),
          Promise.all(commPromises),
        ]);
        if (cancelled) return;

        const memberById = Object.fromEntries(selectedOrg.members.map(m => [m.id, m]));
        const allDeals = dealsSnaps.flatMap((snap, i) =>
          snap.docs.map(d => ({ id: d.id, _ownerId: memberIds[i], _ownerName: memberById[memberIds[i]]?.name || '—', ...d.data() }))
        );
        const allPrequals = prequalsSnaps.flatMap((snap, i) =>
          snap.docs.map(d => ({ id: d.id, _ownerId: memberIds[i], _ownerName: memberById[memberIds[i]]?.name || '—', ...d.data() }))
        ).filter((item) => item.archived !== true);
        const allComms = commsSnaps.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));

        setOrgDeals(allDeals);
        setOrgPrequals(allPrequals);
        setOrgComms(allComms.sort((a, b) => {
          const ad = a.createdAt?.toDate?.() || 0;
          const bd = b.createdAt?.toDate?.() || 0;
          return bd - ad;
        }));
      } catch (err) {
        console.error('Error loading org detail:', err);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedOrg, db]);

  const visibleDeals = useMemo(
    () => (selectedBrokerId ? orgDeals.filter(d => d._ownerId === selectedBrokerId) : orgDeals),
    [orgDeals, selectedBrokerId]
  );
  const visiblePrequals = useMemo(
    () => (selectedBrokerId ? orgPrequals.filter(p => p._ownerId === selectedBrokerId) : orgPrequals),
    [orgPrequals, selectedBrokerId]
  );

  const handleBulkRemoveSelected = async () => {
    if (totalSelected === 0) return;
    const targetOrgs = organizations.filter((o) => selectedOrgIds.has(o.id));
    const targetPending = pendingBrokers.filter((p) => selectedPendingIds.has(p.id));
    const totalBrokers = targetOrgs.reduce((sum, o) => sum + o.members.length, 0);
    const parts = [];
    if (targetOrgs.length) parts.push(`${targetOrgs.length} brokerage${targetOrgs.length === 1 ? '' : 's'} (${totalBrokers} broker account${totalBrokers === 1 ? '' : 's'})`);
    if (targetPending.length) parts.push(`${targetPending.length} pending broker${targetPending.length === 1 ? '' : 's'}`);
    if (!window.confirm(
      `Hide ${parts.join(' and ')} from this tab?\n\nThe accounts and deals stay intact — they just won't show up on the Brokers page or Follow-ups anymore.`
    )) return;
    setBulkRemoving(true);
    try {
      const allMemberIds = targetOrgs.flatMap((o) => o.members.map((m) => m.id));
      await Promise.all([
        ...allMemberIds.map((id) =>
          updateDoc(doc(db, 'brokers', id), {
            hideFromBrokerTab: true,
            hiddenFromBrokerTabAt: serverTimestamp(),
            hiddenFromBrokerTabBy: 'Ben',
          })
        ),
        ...targetPending.map((p) =>
          updateDoc(doc(db, 'pendingBrokerSignups', p.id), {
            hideFromBrokerTab: true,
            hiddenFromBrokerTabAt: serverTimestamp(),
            hiddenFromBrokerTabBy: 'Ben',
          })
        ),
      ]);
      const hidden = new Set(allMemberIds);
      setBrokers((prev) => prev.filter((b) => !hidden.has(b.id)));
      if (targetPending.length && onHidePendingBrokers) {
        onHidePendingBrokers(targetPending.map((p) => p.id));
      }
      exitSelectMode();
    } catch (err) {
      console.error('Bulk hide failed:', err);
      alert('Could not hide some brokerages: ' + (err.message || 'unknown'));
    } finally {
      setBulkRemoving(false);
    }
  };

  const handleDismissPending = async (pending, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const label = (pending.firstName && pending.lastName ? `${pending.firstName} ${pending.lastName}` : '')
      || pending.name
      || pending.email
      || 'this pending broker';
    if (!window.confirm(`Hide ${label} from the Brokers tab + Follow-ups? The lead still exists in pendingBrokerSignups, just won't display here.`)) return;
    try {
      await updateDoc(doc(db, 'pendingBrokerSignups', pending.id), {
        hideFromBrokerTab: true,
        hiddenFromBrokerTabAt: serverTimestamp(),
        hiddenFromBrokerTabBy: 'Ben',
      });
      if (onHidePendingBrokers) onHidePendingBrokers([pending.id]);
    } catch (err) {
      console.error('Hide pending broker failed:', err);
      alert('Could not hide this pending broker. ' + (err.message || ''));
    }
  };

  const handleDismissOrg = async (org, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const label = org.brokerageName || org.creator?.name || 'this brokerage';
    const teamSize = org.members.length;
    const memberPhrase = teamSize === 1 ? 'this broker' : `all ${teamSize} brokers in this org`;
    if (!window.confirm(`Hide ${label} from the Brokers tab? This marks ${memberPhrase} as hidden — the broker accounts and their deals stay intact, they just won't show up here anymore.`)) return;
    try {
      await Promise.all(org.members.map(m =>
        updateDoc(doc(db, 'brokers', m.id), {
          hideFromBrokerTab: true,
          hiddenFromBrokerTabAt: serverTimestamp(),
          hiddenFromBrokerTabBy: 'Ben',
        })
      ));
      const hiddenIds = new Set(org.members.map(m => m.id));
      setBrokers(prev => prev.filter(b => !hiddenIds.has(b.id)));
    } catch (err) {
      console.error('Error hiding brokerage:', err);
      alert('Could not hide this brokerage. ' + (err.message || ''));
    }
  };

  // Rename brokerage: writes the new name onto every member of the org so
  // the change shows up everywhere a member is rendered (Brokers tab,
  // BrokersList, prequal cards that pull from brokers/{id}.brokerageName, etc.).
  const handleSaveBrokerageName = async () => {
    const trimmed = brokerageNameDraft.trim();
    if (!selectedOrg) return;
    if (!trimmed) {
      alert('Brokerage name cannot be empty.');
      return;
    }
    if (trimmed === selectedOrg.brokerageName) {
      setEditingBrokerageName(false);
      return;
    }
    setSavingBrokerageName(true);
    try {
      const memberIds = selectedOrg.members.map((m) => m.id);
      await Promise.all(memberIds.map((id) =>
        updateDoc(doc(db, 'brokers', id), {
          brokerageName: trimmed,
          brokerageNameUpdatedAt: serverTimestamp(),
          brokerageNameUpdatedBy: 'Ben',
        })
      ));
      const ids = new Set(memberIds);
      setBrokers((prev) => prev.map((b) => (ids.has(b.id) ? { ...b, brokerageName: trimmed } : b)));
      setEditingBrokerageName(false);
    } catch (err) {
      console.error('Rename brokerage failed:', err);
      alert('Could not rename brokerage: ' + (err.message || ''));
    } finally {
      setSavingBrokerageName(false);
    }
  };

  // Delete a deal or prequalification for a broker. Removes the Firestore doc
  // AND drops the id from the owner broker's loanApplications /
  // prequalificationApplications array so the broker portal stops listing it.
  const handleDeleteRecord = async (record, kind) => {
    const niceKind = kind === 'deal' ? 'full deal' : 'prequalification';
    const label = record.businessName
      || `${record.firstName || ''} ${record.lastName || ''}`.trim()
      || record.email
      || `this ${niceKind}`;
    if (!window.confirm(`Permanently delete ${niceKind} "${label}"?\n\nThis removes the record from Firestore — it can't be undone.`)) return;
    setDeletingRecordId(record.id);
    try {
      const ownerId = record._ownerId || record.brokerId || selectedOrg?.creator?.id;
      const collectionName = kind === 'deal' ? 'loanApplications' : 'prequalificationApplications';
      await deleteDoc(doc(db, collectionName, record.id));
      if (ownerId) {
        const arrayField = kind === 'deal' ? 'loanApplications' : 'prequalificationApplications';
        try {
          await updateDoc(doc(db, 'brokers', ownerId), { [arrayField]: arrayRemove(record.id) });
        } catch (linkErr) {
          // Non-fatal: the broker doc may not maintain this array — log and move on.
          console.warn(`Could not remove ${record.id} from brokers/${ownerId}.${arrayField}:`, linkErr.message || linkErr);
        }
      }
      if (kind === 'deal') {
        setOrgDeals((prev) => prev.filter((d) => d.id !== record.id));
      } else {
        setOrgPrequals((prev) => prev.filter((p) => p.id !== record.id));
      }
    } catch (err) {
      console.error('Delete record failed:', err);
      alert(`Could not delete this ${niceKind}: ` + (err.message || ''));
    } finally {
      setDeletingRecordId(null);
    }
  };

  // -------- Files for a deal/prequal --------
  const toggleExpand = async (rec, kind) => {
    const nextOpen = expandedId === rec.id ? null : rec.id;
    setExpandedId(nextOpen);
    if (!nextOpen) return;
    if (expandedFiles[rec.id]) return;
    try {
      const col = kind === 'prequal' ? 'prequalificationMetadata' : 'documentMetadata';
      const idField = kind === 'prequal' ? 'applicationId' : 'loanApplicationId';
      const snap = await getDocs(query(collection(db, col), where(idField, '==', rec.id)));
      setExpandedFiles(prev => ({ ...prev, [rec.id]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    } catch (err) {
      console.error('Error loading files:', err);
      setExpandedFiles(prev => ({ ...prev, [rec.id]: [] }));
    }
  };

  const uploadFileFor = async (rec, kind, file, label) => {
    setUploadingFor(rec.id);
    try {
      const ownerId = rec._ownerId || selectedOrg?.creator?.id;
      const path = kind === 'prequal'
        ? `prequalifications/admin/${rec.id}/${label}/${file.name}`
        : `broker-deals/${ownerId}/${rec.id}/${label}/${file.name}`;
      const sRef = storageRef(storage, path);
      const task = uploadBytesResumable(sRef, file);
      await new Promise((resolve, reject) => task.on('state_changed', null, reject, resolve));
      const url = await getDownloadURL(task.snapshot.ref);
      const meta = {
        documentName: label,
        label,
        fileName: file.name,
        fileUrl: url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'admin',
        storagePath: path,
        ...(kind === 'prequal'
          ? { applicationId: rec.id, brokerId: ownerId, status: 'uploaded' }
          : { loanApplicationId: rec.id, uploadLinkId: rec.id, brokerId: ownerId, email: rec.email || '' }
        ),
      };
      const col = kind === 'prequal' ? 'prequalificationMetadata' : 'documentMetadata';
      const ref = await addDoc(collection(db, col), meta);
      setExpandedFiles(prev => ({ ...prev, [rec.id]: [...(prev[rec.id] || []), { id: ref.id, ...meta }] }));
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed: ' + (err.message || 'unknown'));
    } finally {
      setUploadingFor(null);
    }
  };

  // -------- Organizations List View (action-oriented dashboard) --------
  if (!selectedOrg) {
    // Bucket every org into one of: brand_new / needs_first_listing / active / going_cold.
    // Buckets are mutually exclusive so each org appears once.
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
    const orgCreatedAt = (org) => org.creator?.createdAt?.toDate?.() || (org.creator?.createdAt ? new Date(org.creator.createdAt) : null);
    const bucketOf = (org) => {
      const created = orgCreatedAt(org);
      if (created && now - created.getTime() <= sevenDaysMs) return 'brand_new';
      const totalActivity = (org.totalDeals || 0) + (org.totalPrequals || 0);
      if (totalActivity === 0) return 'needs_first_listing';
      const last = org.lastActive ? org.lastActive.getTime() : 0;
      if (now - last > fourteenDaysMs) return 'going_cold';
      return 'active';
    };

    const buckets = { brand_new: [], needs_first_listing: [], active: [], going_cold: [] };
    organizations.forEach((org) => {
      buckets[bucketOf(org)].push(org);
    });

    // Sort within each bucket: recent for brand_new + active, oldest activity for going_cold.
    buckets.brand_new.sort((a, b) => (orgCreatedAt(b)?.getTime() || 0) - (orgCreatedAt(a)?.getTime() || 0));
    buckets.needs_first_listing.sort((a, b) => (orgCreatedAt(b)?.getTime() || 0) - (orgCreatedAt(a)?.getTime() || 0));
    buckets.active.sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
    buckets.going_cold.sort((a, b) => (a.lastActive || 0) - (b.lastActive || 0));

    const totalBrokers = brokers.length;
    const newThisWeek = brokers.filter((b) => {
      const c = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : null);
      return c && now - c.getTime() <= sevenDaysMs;
    }).length;
    const withListings = organizations.filter((o) => (o.totalDeals + o.totalPrequals) > 0).length;
    const withoutListings = organizations.length - withListings;

    const renderOrgCard = (org, accent) => {
      const isSelected = selectedOrgIds.has(org.id);
      const handleCardClick = () => {
        if (selectMode) {
          toggleOrgSelection(org.id);
        } else {
          setSelectedOrgId(org.id);
          setSelectedBrokerId(null);
        }
      };
      return (
        <div
          key={org.id}
          onClick={handleCardClick}
          className={`group relative text-left bg-white rounded-lg border transition-all p-3 cursor-pointer ${
            isSelected ? 'border-indigo-500 ring-2 ring-indigo-300' : `${accent.border} hover:shadow-md`
          }`}
        >
          {selectMode ? (
            <div
              className={`absolute top-1.5 right-1.5 h-5 w-5 rounded border-2 flex items-center justify-center z-10 ${
                isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white'
              }`}
              aria-hidden="true"
            >
              {isSelected && <CheckIcon className="h-3.5 w-3.5" />}
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => handleDismissOrg(org, e)}
              title="Hide from Brokers tab (the broker accounts stay intact)"
              className="absolute top-1.5 right-1.5 p-1 rounded-full text-gray-300 hover:bg-gray-100 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="min-w-0 pr-5">
            <div className="font-semibold text-gray-900 truncate text-sm">{org.brokerageName}</div>
            {org.creator && (
              <div className="text-[11px] text-gray-500 truncate">
                {org.creator.name}{org.subBrokers.length > 0 ? ` + ${org.subBrokers.length}` : ''}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] mt-2 flex-wrap">
            {org.creator?.inviteStatus && org.creator.inviteStatus !== 'activated' && (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); openBrokerInvite(org.creator); }}
                className={`px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${
                  org.creator.inviteStatus === 'sent'
                    ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
              >
                <PaperAirplaneIcon className="h-3 w-3" />
                {org.creator.inviteStatus === 'sent' ? 'Invite sent' : 'Invite draft'}
              </button>
            )}
            {org.creator?.inviteStatus === 'activated' && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">Activated</span>
            )}
            {org.totalDeals > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                {org.totalDeals} {org.totalDeals === 1 ? 'deal' : 'deals'}
              </span>
            )}
            {org.totalPrequals > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                {org.totalPrequals} {org.totalPrequals === 1 ? 'prequal' : 'prequals'}
              </span>
            )}
            {org.lastActive && <span className="text-gray-400 ml-auto text-[10px]">{formatDate(org.lastActive)}</span>}
          </div>
        </div>
      );
    };

    const sections = [
      {
        key: 'brand_new', items: buckets.brand_new,
        title: 'New this week', subtitle: 'Brokerages that signed up in the last 7 days — get them onboarded with their first listing.',
        accent: { headerBg: 'bg-gradient-to-r from-emerald-50 to-teal-50', headerBorder: 'border-emerald-200', icon: SparklesIcon, iconColor: 'text-emerald-600', titleColor: 'text-emerald-900', countBg: 'bg-emerald-500 text-white', border: 'border-emerald-200' },
      },
      {
        key: 'needs_first_listing', items: buckets.needs_first_listing,
        title: 'Needs first listing', subtitle: 'Brokerages with zero listings or deals — push them to upload something.',
        accent: { headerBg: 'bg-gradient-to-r from-amber-50 to-orange-50', headerBorder: 'border-amber-200', icon: ExclamationTriangleIcon, iconColor: 'text-amber-600', titleColor: 'text-amber-900', countBg: 'bg-amber-500 text-white', border: 'border-amber-200' },
      },
      {
        key: 'going_cold', items: buckets.going_cold,
        title: 'Going cold', subtitle: '14+ days since last listing or activity. Re-engage before they drop off completely.',
        accent: { headerBg: 'bg-gradient-to-r from-rose-50 to-pink-50', headerBorder: 'border-rose-200', icon: ClockSolidIcon, iconColor: 'text-rose-600', titleColor: 'text-rose-900', countBg: 'bg-rose-500 text-white', border: 'border-rose-200' },
      },
      {
        key: 'active', items: buckets.active,
        title: 'Active & in flight', subtitle: 'Recent listing or buyer activity in the last 14 days — keep momentum, check on buyers.',
        accent: { headerBg: 'bg-gradient-to-r from-indigo-50 to-violet-50', headerBorder: 'border-indigo-200', icon: BoltIcon, iconColor: 'text-indigo-600', titleColor: 'text-indigo-900', countBg: 'bg-indigo-500 text-white', border: 'border-indigo-200' },
      },
    ];

    // Optional search filter — when active, replace section view with single grid.
    const trimmedSearch = searchTerm.trim().toLowerCase();
    const searchMatches = trimmedSearch
      ? organizations.filter((o) =>
          (o.brokerageName || '').toLowerCase().includes(trimmedSearch) ||
          (o.creator?.name || '').toLowerCase().includes(trimmedSearch) ||
          (o.creator?.email || '').toLowerCase().includes(trimmedSearch) ||
          o.members.some((m) => m.name.toLowerCase().includes(trimmedSearch) || (m.email || '').toLowerCase().includes(trimmedSearch))
        )
      : null;

    return (
      <div className="space-y-4">
        {showBrokerInvite && (
          <BrokerInviteModal
            initialBroker={brokerInviteSeed}
            onClose={() => setShowBrokerInvite(false)}
            onCreated={handleBrokerCreated}
          />
        )}
        {/* Hero stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <BuildingOfficeIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">Total brokers</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{totalBrokers}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <SparklesIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">New this week</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{newThisWeek}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <CheckCircleIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">With listings</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{withListings}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <ExclamationTriangleIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">No listings yet</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{withoutListings}</p>
          </div>
        </div>

        {/* Search + select-mode toolbar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by brokerage, broker name, or email…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          {trimmedSearch && !selectMode && (
            <span className="text-xs text-gray-500">{searchMatches.length} match{searchMatches.length === 1 ? '' : 'es'}</span>
          )}
          {!selectMode && (
            <button
              type="button"
              onClick={() => openBrokerInvite()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
            >
              <PlusIcon className="h-4 w-4" />
              Create broker account
            </button>
          )}
          {selectMode ? (
            <>
              <span className="text-xs font-medium text-gray-600">
                {totalSelected} selected
              </span>
              <button
                type="button"
                onClick={handleBulkRemoveSelected}
                disabled={totalSelected === 0 || bulkRemoving}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {bulkRemoving ? 'Hiding…' : `Hide ${totalSelected || ''}`.trim()}
              </button>
              <button
                type="button"
                onClick={exitSelectMode}
                disabled={bulkRemoving}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setSelectMode(true)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Select &amp; remove
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-sm text-gray-400">Loading brokerages…</div>
        ) : trimmedSearch ? (
          searchMatches.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">No brokerages match "{trimmedSearch}".</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {searchMatches.map((org) => renderOrgCard(org, { border: 'border-gray-200' }))}
            </div>
          )
        ) : organizations.length === 0 && pendingBrokers.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">No brokerages yet.</div>
        ) : (
          <div className="space-y-4">
            {/* Pending brokers — cold-called but haven't created accounts yet.
                Lives above the signed-up sections so Ben sees the top-of-funnel first. */}
            {pendingBrokers.length > 0 && (
              <section className="rounded-xl border border-purple-200 overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-fuchsia-50 border-b border-purple-200 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <UserGroupIcon className="h-5 w-5 flex-shrink-0 text-purple-600" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-purple-900">Pending brokers</h3>
                      <p className="text-[11px] text-purple-900 opacity-70 truncate">Cold-called but haven&apos;t created broker-portal accounts yet — push them to sign up.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-purple-500 text-white">
                      {pendingBrokers.length}
                    </span>
                    {onOpenFollowups && (
                      <button
                        type="button"
                        onClick={onOpenFollowups}
                        className="text-[11px] font-medium text-purple-700 hover:text-purple-900"
                      >
                        Open Follow-ups →
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-white p-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                    {pendingBrokers.map((pb) => {
                      const displayName = (pb.firstName && pb.lastName ? `${pb.firstName} ${pb.lastName}` : '')
                        || pb.name
                        || pb.brokerageName
                        || pb.email
                        || 'Unknown';
                      const isSelected = selectedPendingIds.has(pb.id);
                      const handlePendingClick = () => {
                        if (selectMode) {
                          togglePendingSelection(pb.id);
                        } else if (onOpenFollowups) {
                          onOpenFollowups();
                        }
                      };
                      return (
                        <div
                          key={pb.id}
                          onClick={handlePendingClick}
                          className={`group relative rounded-lg border p-3 transition-all ${
                            isSelected
                              ? 'border-indigo-500 ring-2 ring-indigo-300 bg-white'
                              : 'border-purple-200 bg-purple-50/40 hover:shadow-md hover:bg-purple-50'
                          } ${(selectMode || onOpenFollowups) ? 'cursor-pointer' : ''}`}
                        >
                          {selectMode ? (
                            <div
                              className={`absolute top-1.5 right-1.5 h-5 w-5 rounded border-2 flex items-center justify-center z-10 ${
                                isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white'
                              }`}
                              aria-hidden="true"
                            >
                              {isSelected && <CheckIcon className="h-3.5 w-3.5" />}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => handleDismissPending(pb, e)}
                              title="Hide from Brokers tab + Follow-ups (lead still exists in pendingBrokerSignups)"
                              className="absolute top-1.5 right-1.5 p-1 rounded-full text-gray-300 hover:bg-gray-100 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <div className="min-w-0 pr-5">
                            <div className="font-semibold text-gray-900 truncate text-sm">{displayName}</div>
                            {pb.email && <div className="text-[11px] text-gray-500 truncate">{pb.email}</div>}
                            {(pb.phoneNumber || pb.phone) && (
                              <div className="text-[11px] text-gray-400 truncate">{pb.phoneNumber || pb.phone}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-[10px] mt-2 flex-wrap">
                            <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                              {pb.reminderCount || 0} reminder{(pb.reminderCount || 0) === 1 ? '' : 's'}
                            </span>
                            {pb.createdAt && (
                              <span className="text-gray-400 ml-auto text-[10px]">
                                Added {formatDate(pb.createdAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {sections.map((section) => (
              <section key={section.key} className={`rounded-xl border ${section.accent.headerBorder} overflow-hidden`}>
                <div className={`px-4 py-3 ${section.accent.headerBg} border-b ${section.accent.headerBorder} flex items-center justify-between`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <section.accent.icon className={`h-5 w-5 flex-shrink-0 ${section.accent.iconColor}`} />
                    <div className="min-w-0">
                      <h3 className={`text-sm font-bold ${section.accent.titleColor}`}>{section.title}</h3>
                      <p className={`text-[11px] ${section.accent.titleColor} opacity-70 truncate`}>{section.subtitle}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${section.accent.countBg} flex-shrink-0`}>
                    {section.items.length}
                  </span>
                </div>
                <div className="bg-white p-3">
                  {section.items.length === 0 ? (
                    <p className="text-xs text-gray-400 italic text-center py-3">No brokerages here right now.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                      {section.items.map((org) => renderOrgCard(org, section.accent))}
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    );
  }

  // -------- Organization Detail View --------
  const dealBrokerId = focusedMembers.length === 1 ? focusedMembers[0].id : selectedOrg.creator?.id;
  const submitDealUrl = dealBrokerId ? `/broker-deal/${dealBrokerId}` : null;
  const addPrequalUrl = dealBrokerId ? `/admin/new-listing?type=broker&id=${dealBrokerId}` : null;
  const focusBroker = selectedBrokerId ? selectedOrg.members.find(m => m.id === selectedBrokerId) : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <button
            onClick={() => { setSelectedOrgId(null); setSelectedBrokerId(null); }}
            className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeftIcon className="h-3 w-3 mr-1" />
            All brokerages
          </button>
          {editingBrokerageName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={brokerageNameDraft}
                onChange={(e) => setBrokerageNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveBrokerageName();
                  if (e.key === 'Escape') { setEditingBrokerageName(false); setBrokerageNameDraft(''); }
                }}
                disabled={savingBrokerageName}
                autoFocus
                className="text-2xl font-bold text-gray-900 border-b-2 border-indigo-400 focus:outline-none focus:border-indigo-600 bg-transparent flex-1 max-w-md"
              />
              <button
                type="button"
                onClick={handleSaveBrokerageName}
                disabled={savingBrokerageName || !brokerageNameDraft.trim()}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingBrokerageName ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => { setEditingBrokerageName(false); setBrokerageNameDraft(''); }}
                disabled={savingBrokerageName}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h2 className="text-2xl font-bold text-gray-900">{selectedOrg.brokerageName}</h2>
              <button
                type="button"
                onClick={() => { setBrokerageNameDraft(selectedOrg.brokerageName || ''); setEditingBrokerageName(true); }}
                title="Edit brokerage name"
                className="opacity-0 group-hover:opacity-100 transition-opacity text-xs font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
              >
                Rename
              </button>
            </div>
          )}
          {selectedOrg.creator && (
            <div className="mt-1 text-sm text-gray-500 space-x-3">
              <span>Creator: {selectedOrg.creator.name}</span>
              {selectedOrg.creator.email && <span>{selectedOrg.creator.email}</span>}
              {selectedOrg.creator.phoneNumber && <span>{selectedOrg.creator.phoneNumber}</span>}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {addPrequalUrl && (
            <a
              href={addPrequalUrl}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add prequalification{focusBroker ? ` for ${focusBroker.firstName || focusBroker.name}` : ''}
            </a>
          )}
          {submitDealUrl && (
            <button
              onClick={() => window.open(submitDealUrl, '_blank')}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add full deal
            </button>
          )}
          {selectedOrg.creator?.email && (
            <a
              href={`mailto:${selectedOrg.creator.email}`}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <EnvelopeIcon className="h-4 w-4" />
              Email creator
            </a>
          )}
        </div>
      </div>

      {/* Team panel: creator + sub-brokers */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-3">
          <UserGroupIcon className="h-4 w-4 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">Team</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">{selectedOrg.members.length}</span>
          {selectedBrokerId && (
            <button
              onClick={() => setSelectedBrokerId(null)}
              className="ml-auto text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Show whole team
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {selectedOrg.creator && (
            <TeamMemberCard
              member={selectedOrg.creator}
              isCreator
              isFocused={selectedBrokerId === selectedOrg.creator.id}
              onFocus={() => setSelectedBrokerId(
                selectedBrokerId === selectedOrg.creator.id ? null : selectedOrg.creator.id
              )}
            />
          )}
          {selectedOrg.subBrokers.map(sub => (
            <TeamMemberCard
              key={sub.id}
              member={sub}
              isFocused={selectedBrokerId === sub.id}
              onFocus={() => setSelectedBrokerId(selectedBrokerId === sub.id ? null : sub.id)}
            />
          ))}
        </div>
      </section>

      {/* Quick stats (reflect current focus) */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl ring-1 ring-emerald-200 bg-emerald-50 text-emerald-700 p-4">
          <p className="text-xs font-medium uppercase tracking-wider opacity-70">Full Deals</p>
          <p className="mt-1 text-3xl font-bold leading-none">{visibleDeals.length}</p>
        </div>
        <div className="rounded-xl ring-1 ring-indigo-200 bg-indigo-50 text-indigo-700 p-4">
          <p className="text-xs font-medium uppercase tracking-wider opacity-70">Prequalifications</p>
          <p className="mt-1 text-3xl font-bold leading-none">{visiblePrequals.length}</p>
        </div>
        <div className="rounded-xl ring-1 ring-amber-200 bg-amber-50 text-amber-700 p-4">
          <p className="text-xs font-medium uppercase tracking-wider opacity-70">Emails Sent</p>
          <p className="mt-1 text-3xl font-bold leading-none">{orgComms.length}</p>
        </div>
      </div>

      {detailLoading ? (
        <div className="text-center py-12 text-sm text-gray-400">Loading deals…</div>
      ) : (
        <>
          {/* Full deals */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Full Deals Submitted{focusBroker ? ` — ${focusBroker.name}` : ''}
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{visibleDeals.length}</span>
            </div>
            {visibleDeals.length === 0 ? (
              <p className="text-xs text-gray-400">No full deals yet.</p>
            ) : (
              <div className="space-y-2">
                {visibleDeals.map(deal => {
                  const isOpen = expandedId === deal.id;
                  const files = expandedFiles[deal.id] || [];
                  return (
                    <DealRow
                      key={deal.id}
                      record={deal}
                      kind="deal"
                      ownerLabel={!selectedBrokerId && selectedOrg.members.length > 1 ? deal._ownerName : null}
                      isOpen={isOpen}
                      files={files}
                      onToggle={() => toggleExpand(deal, 'deal')}
                      onUpload={(f, l) => uploadFileFor(deal, 'deal', f, l)}
                      uploading={uploadingFor === deal.id}
                      onDelete={() => handleDeleteRecord(deal, 'deal')}
                      deleting={deletingRecordId === deal.id}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* Prequalifications */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Prequalifications{focusBroker ? ` — ${focusBroker.name}` : ''}
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">{visiblePrequals.length}</span>
            </div>
            {visiblePrequals.length === 0 ? (
              <p className="text-xs text-gray-400">No prequalifications yet.</p>
            ) : (
              <div className="space-y-2">
                {visiblePrequals.map(p => {
                  const isOpen = expandedId === p.id;
                  const files = expandedFiles[p.id] || [];
                  return (
                    <DealRow
                      key={p.id}
                      record={p}
                      kind="prequal"
                      ownerLabel={!selectedBrokerId && selectedOrg.members.length > 1 ? p._ownerName : null}
                      isOpen={isOpen}
                      files={files}
                      onToggle={() => toggleExpand(p, 'prequal')}
                      onUpload={(f, l) => uploadFileFor(p, 'prequal', f, l)}
                      uploading={uploadingFor === p.id}
                      onDelete={() => handleDeleteRecord(p, 'prequal')}
                      deleting={deletingRecordId === p.id}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* Communications */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <EnvelopeIcon className="h-4 w-4 text-gray-500" />
              <h3 className="text-base font-semibold text-gray-900">Recent Communications</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">{orgComms.length}</span>
            </div>
            {orgComms.length === 0 ? (
              <p className="text-xs text-gray-400">No emails on record.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {orgComms.slice(0, 25).map(c => (
                  <div key={c.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.subject || c.message?.subject || '(no subject)'}</p>
                      <p className="text-xs text-gray-500 truncate">To {Array.isArray(c.to) ? c.to.join(', ') : c.to || '—'} • {formatDate(c.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function BrokerInviteModal({ initialBroker, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: initialBroker?.name || '',
    email: initialBroker?.email || '',
    phoneNumber: initialBroker?.phoneNumber || '',
    brokerageName: initialBroker?.brokerageName || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const provision = async (sendInvite) => {
    setSubmitting(true);
    setError('');
    try {
      const createInvite = httpsCallable(functions, 'createBrokerInvite');
      const response = await createInvite({ ...form, sendInvite });
      const data = response.data || {};
      setResult(data);
      onCreated({
        id: data.brokerId,
        userId: data.brokerId,
        ...form,
        name: form.name,
        firstName: form.name.trim().split(/\s+/)[0] || '',
        lastName: form.name.trim().split(/\s+/).slice(1).join(' '),
        status: 'invited',
        inviteStatus: data.inviteStatus,
        dealsCount: initialBroker?.dealsCount || 0,
        prequalsCount: initialBroker?.prequalsCount || 0,
        createdAt: initialBroker?.createdAt || new Date(),
        lastActive: initialBroker?.lastActive || null,
      });
    } catch (err) {
      console.error('Unable to create broker invitation:', err);
      setError(err?.message || 'Could not create the broker invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.setupLink) return;
    try {
      await navigator.clipboard.writeText(result.setupLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Copy failed. Select the link below and copy it manually.');
    }
  };

  const isValid = form.name.trim() && /^\S+@\S+\.\S+$/.test(form.email.trim());

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 p-4" onMouseDown={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">Broker access</p>
            <h2 className="mt-1 text-xl font-bold text-slate-900">
              {initialBroker ? 'Manage broker invitation' : 'Create broker account'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">They choose their own password from a secure, one-time setup link.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
              Broker name
              <input value={form.name} onChange={update('name')} placeholder="Jane Smith" className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
            </label>
            <label className="text-xs font-semibold text-slate-700">
              Email
              <input type="email" value={form.email} onChange={update('email')} placeholder="jane@brokerage.com" className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
            </label>
            <label className="text-xs font-semibold text-slate-700">
              Phone
              <input value={form.phoneNumber} onChange={update('phoneNumber')} placeholder="(555) 555-5555" className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
            </label>
            <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
              Brokerage
              <input value={form.brokerageName} onChange={update('brokerageName')} placeholder="Acme Business Brokers" className="mt-1 w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
            </label>
          </div>

          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

          {result?.setupLink && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-emerald-900">
                <CheckCircleIcon className="h-5 w-5" />
                {result.inviteStatus === 'sent' ? 'Account created and invitation sent' : 'Account created — link ready'}
              </div>
              <p className="mt-1 text-xs text-emerald-800">This link lets the broker set their password and then sends them to Lended sign-in.</p>
              <div className="mt-3 flex gap-2">
                <input readOnly value={result.setupLink} onFocus={(event) => event.target.select()} className="min-w-0 flex-1 rounded-lg border-emerald-200 bg-white text-xs" />
                <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-300 hover:bg-emerald-100">
                  <LinkIcon className="h-4 w-4" /> {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Done</button>
          <button type="button" disabled={!isValid || submitting} onClick={() => provision(false)} className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-40">
            <LinkIcon className="h-4 w-4" /> {submitting ? 'Working…' : 'Create & copy link'}
          </button>
          <button type="button" disabled={!isValid || submitting} onClick={() => provision(true)} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
            <PaperAirplaneIcon className="h-4 w-4" /> {submitting ? 'Working…' : initialBroker || result ? 'Send / resend invite' : 'Create & send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Team member card ----
function TeamMemberCard({ member, isCreator, isFocused, onFocus }) {
  return (
    <button
      onClick={onFocus}
      className={`text-left rounded-lg border p-3 transition-all ${
        isFocused
          ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50'
          : 'border-gray-200 hover:border-indigo-300 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
          isCreator ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
        }`}>
          {isCreator ? <StarIcon className="h-4 w-4" /> : <UserCircleIcon className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-gray-900 truncate">{member.name}</p>
            {isCreator && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                Creator
              </span>
            )}
          </div>
          {member.email && <p className="text-xs text-gray-500 truncate">{member.email}</p>}
          <div className="mt-1 flex items-center gap-1.5 text-[10px]">
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
              {member.dealsCount || 0} deals
            </span>
            <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
              {member.prequalsCount || 0} prequals
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

// ---- Deal/Prequal row component ----
function DealRow({ record, kind, ownerLabel, isOpen, files, onToggle, onUpload, uploading, onDelete, deleting }) {
  const [docType, setDocType] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [file, setFile] = useState(null);

  const title = kind === 'deal'
    ? `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.businessName || 'Untitled Deal'
    : record.businessName || 'Untitled Prequalification';
  const subtitle = kind === 'deal'
    ? [record.email, record.businessName, record.purchasePrice ? fmt$(record.purchasePrice) : null].filter(Boolean).join(' · ')
    : [record.businessState, record.industry, record.purchasePrice ? fmt$(record.purchasePrice) : null].filter(Boolean).join(' · ');
  const statusBadge = kind === 'deal'
    ? (record.allDocumentsUploaded ? { text: 'All docs', color: 'bg-green-100 text-green-700' }
      : record.allPersonalFilesUploaded ? { text: 'Personal done', color: 'bg-blue-100 text-blue-700' }
      : { text: 'Awaiting docs', color: 'bg-orange-100 text-orange-700' })
    : (record.prequalifiedAmount ? { text: `Prequalified ${fmt$(record.prequalifiedAmount)}`, color: 'bg-emerald-100 text-emerald-700' }
      : { text: record.status || 'In review', color: 'bg-gray-100 text-gray-700' });

  const handleSubmitUpload = async () => {
    if (!file) return;
    const label = (customLabel && customLabel.trim()) || docType;
    if (!label) return;
    await onUpload(file, label);
    setFile(null);
    setDocType('');
    setCustomLabel('');
  };

  return (
    <div className="group rounded-lg border border-gray-200 hover:border-indigo-200 transition-colors flex items-stretch">
      <button
        onClick={onToggle}
        className="flex-1 text-left px-4 py-3 flex items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <div className="font-medium text-sm text-gray-900 truncate">{title}</div>
          {subtitle && <div className="text-xs text-gray-500 truncate">{subtitle}</div>}
          {ownerLabel && (
            <div className="text-[10px] text-gray-400 truncate mt-0.5">Owned by {ownerLabel}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge.color}`}>{statusBadge.text}</span>
          {onDelete && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); if (!deleting) onDelete(); }}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !deleting) { e.preventDefault(); onDelete(); } }}
              title={`Delete this ${kind === 'deal' ? 'full deal' : 'prequalification'} (cannot be undone)`}
              className={`text-xs font-medium px-2 py-0.5 rounded-md text-rose-600 hover:bg-rose-50 transition-opacity opacity-0 group-hover:opacity-100 ${deleting ? 'opacity-100 cursor-wait' : ''}`}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </span>
          )}
          {isOpen ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-100 space-y-3">
          <div className="pt-3">
            <div className="flex items-center gap-2 mb-2">
              <DocumentTextIcon className="h-4 w-4 text-gray-500" />
              <span className="text-xs font-semibold text-gray-700">Files</span>
              <span className="text-xs text-gray-400">{files.length}</span>
            </div>
            {files.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No files uploaded yet.</p>
            ) : (
              <div className="space-y-1">
                {files.map(f => (
                  <div key={f.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-1.5 border border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="font-medium text-gray-700 truncate">{f.label || f.documentName}</span>
                      <span className="text-gray-400 truncate">— {f.fileName}</span>
                    </div>
                    {f.fileUrl && (
                      <a href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 flex-shrink-0">View</a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpTrayIcon className="h-4 w-4 text-gray-500" />
              <span className="text-xs font-semibold text-gray-700">Add a file</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <input
                type="text"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                placeholder="Doc type (e.g. 2024 Business Tax Return)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="…or custom name (overrides)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                className="text-xs flex-1 text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              <button
                onClick={handleSubmitUpload}
                disabled={!file || (!docType && !customLabel) || uploading}
                className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
